import { ColumnDetails } from '../../types/common';
import { Filter } from '../../gridFilters/types';
import { GridBaseDataProvider } from '../../GridBaseDataProvider';
import { SelectOption } from '../../../ui/gzSelect/types';
import { GridColumnConfiguration } from '@nirvana/shared/dtos/types/shared/grid';
import { GroupType } from '../../../ui/gzEnumList/types';
import { GridTitleConfig } from '../../uiElements/gridViewTitle/types';
import { GridFilterUiConfiguration } from '../../../types/filters/types';
import { TemplateResult } from 'lit';

export enum ViewType {
  DEFAULT = 0,
  CUSTOM = 1,
}

export interface ViewConfiguration<Column extends number, ColumnKeys extends string, Type extends number = ViewType> {
  id: string;
  name: string;
  type: Type;
  filters: Array<Filter<ColumnKeys>>;
  displayedColumns: Array<Column>;
  compactView: boolean;
  pageSize: number;
  pageNumber?: number;
  columnConfigs?: Array<GridColumnConfiguration>;
  gridSettingsColumnsGroupType?: GroupType;
  isReadOnly?: boolean;
  isRecent?: boolean;
  isFavorite?: boolean;
}

export type GridHeaderRenderers = Record<string, (...args: any[]) => string>;
export type GridColumnRenderers = Record<string, (...args: any[]) => string | Node | TemplateResult>;

export interface GridViewMetadata<Column extends number, ColumnKeys extends string> {
  viewId: string;
  allColumnDetails: ColumnDetails<Column, ColumnKeys>[];
  filters: Filter<ColumnKeys>[];
  predefinedFiltersKeys: ColumnKeys[];
  columns: Column[];
  filterDependencyMapping?: ColumnDetails<Column, ColumnKeys>[];
  isCompactViewChecked: boolean;
  defaultViewProps?: ViewConfiguration<Column, ColumnKeys>;
  pageSize: number;
  pageNumber: number;
  headerRenderers?: GridHeaderRenderers;
  columnRenderers?: GridColumnRenderers;
  rowDetailsRenderer?: Function;
  dataProvider: GridBaseDataProvider<Column, ColumnKeys>;
  removeFilters?: boolean;
  hideHeaderActions?: boolean;
  hideColumnHeaders?: boolean;
  hideFooter?: boolean;
  hasSelectableRows?: boolean;
  hideGridRefresh?: boolean;
  hideFiltersToggle?: boolean;
  hideResetFilters?: boolean;
  disableGridSettings?: boolean;
  inlineRowActions?: Array<SelectOption>;
  columnConfigs: Array<GridColumnConfiguration>;
  defaultFlexGrowColumn?: string;
  enableRowClickEvent?: boolean;
  enableCSVExport?: boolean;
  smartViewsEnabled?: boolean;
  showFilters?: boolean;
  activeViewProps?: ViewConfiguration<Column, ColumnKeys>;
  smartViewDiscardButtonDisabledState?: boolean;
  smartViewSaveButtonDisabledState?: boolean;
  titleConfig?: GridTitleConfig;
  hasPagination?: boolean;
  pageSizeOptions?: Array<SelectOption>;
  noEscapeSpaceInTitle?: boolean;
  showGlobalActions?: boolean;
  showConfigurableArea?: boolean;
  hasSelectionExtraOptions?: boolean;
  sortDirection?: string;
  sortColumnName?: string;
  filtersUiConfigs?: GridFilterUiConfiguration[];
  favoriteIconConfig?: any;
  dynamicHeight?: boolean;
  sideMenuParent?: HTMLElement;
  extraParams?: any;
  selectionStoreConfig?: SelectionStoreConfig;
  cellClassNameGenerator?(column: Column, item: any): string;
}

export interface CustomStrings {
  noSearchResultsString?: string;
  dataLoadingFailed?: string;
}

export const DEFAULT_COLUMN_MIN_WIDTH = 64;
export const DEFAULT_COLUMN_RESET_WIDTH = 100;

export enum GridAttribute {
  TITLE = 'grid-title',
  PROPERTIES_SET = 'data-properties-set',
  SELECTION_EXTRA_OPTIONS = 'selection-extra-options',
}

export enum SelectionOptions {
  ALL = 'all',
  All_FROM_PAGE = 'allFromPage',
}

export interface GridGetColumnConfig {
  sortDirection?: string;
  sortColumnName?: string;
  /**
   * Sort is done via API and should not trigger implicit grid sort behavior
   */
  apiSort?: boolean;
}

export enum SelectionStoreMode {
  IDS = 'arrayOfIds',
  RECORDS = 'arrayOfRecords',
}

export interface FunctionalMode<T> {
  mode: Array<string> | Array<T>;
}

export const SELECTION_STORE_CONFIGURATION: SelectionStoreConfig = {
  maxNumOfItems: 1000,
  uniqueIdentifier: '_id',
  functionalMode: SelectionStoreMode.IDS,
};

export interface SelectionStoreConfig {
  maxNumOfItems: number;
  uniqueIdentifier: string;
  functionalMode: SelectionStoreMode;
}

export interface GridGetColumnConfig {
  sortDirection?: string;
  sortColumnName?: string;
  /**
   * Sort is done via API and should not trigger implicit grid sort behavior
   */
  uiSort?: boolean;
}

export const DEFAULT_PAGE_SIZE = 50;
export const DEFAULT_PAGE_NUMBER = 1;

export const defaultPageSizeOptions = [
  {
    title: '20',
    value: '20',
  },
  {
    title: '50',
    value: '50',
  },
  {
    title: '100',
    value: '100',
  },
];

export interface GridColumnPreset {
  key: string;
  flexGrow: number;
  width?: string;
}

export class RowRightClickEvent<T = {}> extends CustomEvent<{
  coords: Coords;
  selectedItems: Array<T>;
  rowIndex: number;
}> {}

export interface Coords {
  x: number;
  y: number;
}
